import { Identify, identify } from "@amplitude/analytics-browser"

interface LocationData {
  city: string
  state: string
  country: string
}

type LocationProvider = {
  getGeolocationData: () => Promise<LocationData | undefined>
}

declare global {
  interface Window {
    OneTrust: LocationProvider | undefined
  }
}

export type LocationService = Readonly<{
  getLocation(): Promise<LocationData>
}>

const RESOLUTION_TIMEOUT = 2000
const RESOLUTION_INTERVAL = 100
const Noop = (value: unknown) => {}
const waitForOneTrust = async (): Promise<LocationProvider> => {
  const stateObject = {
    timeout: undefined,
    elapse: 0,
    resolve: Noop,
    reject: Noop,
  }
  const oneTrustPromise = new Promise<LocationProvider>((resolve, reject) => {
    Object.assign(stateObject, { resolve, reject })
  })

  const checkPresence = () => {
    stateObject.elapse += RESOLUTION_INTERVAL
    if (stateObject.elapse >= RESOLUTION_TIMEOUT) {
      return stateObject.reject(new Error('OneTrust not available'))
    }

    if (window.OneTrust) {
      return stateObject.resolve(window.OneTrust)
    }

    Object.assign(stateObject, {
      timeout: setTimeout(checkPresence, RESOLUTION_TIMEOUT)
    })
  }

  Object.assign(stateObject, {
    timeout: setTimeout(checkPresence, RESOLUTION_TIMEOUT)
  })

  return oneTrustPromise
}

export const locationService: LocationService = {
  getLocation: async (): Promise<LocationData> => {
    try {
      let locationData: Partial<LocationData> = {}

      if (!locationData.state) {
        // wait for OneTrust  to load
        await waitForOneTrust()

        const oneTrustLocationData = await window.OneTrust?.getGeolocationData()

        if (!oneTrustLocationData) {
          throw new Error('OneTrust location not available')
        }

        const identifyObj = new Identify()
        Object.entries(oneTrustLocationData)
          .filter(([_, value]) => value !== undefined)
          .forEach(([key, value]) => identifyObj.set(key, value))
        identify(identifyObj)

        Object.assign(locationData, oneTrustLocationData)
      }

      return locationData as LocationData
    } catch (error) {
      console.error('Error getting location: ', error)
      return {
        city: '',
        country: '',
        state: '',
      }
    }
  }
}
